import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { TableStore } from 'shared/ui/Table'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import type { IClickToText } from 'entities/ClickToText'
import { ClickToTextApi } from 'entities/ClickToText/api/clickToText'
import type {
  IParamsGetClickToText,
  IResponseGetClickToText,
  IResponseClickToText,
} from 'entities/ClickToText/api/type'
import { ClickToText } from 'entities/ClickToText/model/ClickToText'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { UsersApi } from 'entities/Users'
import { tableLimitStore } from 'entities/TableLimit'

export type ISetIsClickToTextActionShown = (isShown: boolean) => void

export class ClickToTextListStore {
  private _page = 1
  private _total = 0
  private _limit = tableLimitStore.getLimit('click-to-text-table') ?? 10
  private _isInitialLoading = true
  private _clickToTextMap = new Map<number, IClickToText>()
  private _disposeLoadClickToText: IReactionDisposer | null = null
  private _disposeClickToTextActionShown: IReactionDisposer | null = null
  private _disposeRequestParams: IReactionDisposer | null = null
  private _search = ''
  private readonly _tableStore

  constructor(
    setIsClickToTextActionShown: ISetIsClickToTextActionShown,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this.reactionClickToTextActionShown(setIsClickToTextActionShown)
    this.reactionLoadClickToText()
    this._tableStore = new TableStore<IClickToText>({
      saveColumns: this._saveColumns,
      getColumns: this._getColumns(),
      element: 'clickToText',
      sortBy: 'created_at',
      sortOrder: 'desc',
    })
    this._requestLoadClickToText.setLoading(true)
  }

  get requestParams(): IParamsGetClickToText {
    if (this._search) {
      return {
        limit: this.limit || undefined,
        search: this._search,
      }
    }

    return {
      limit: this.limit || undefined,
    }
  }

  get paramsGetItems(): IParamsGetClickToText {
    return {
      page: this._page,
      ...this.requestParams,
    }
  }

  get limit() {
    return this._limit
  }

  get isLoading() {
    return this._requestLoadClickToText.loading
  }

  get isInitialLoading() {
    return this._isInitialLoading
  }

  get page() {
    return this._page
  }

  get tableStore() {
    return this._tableStore
  }

  get total() {
    return this._total
  }

  get isClickToTextEmpty() {
    return !this.isLoading && !this._clickToTextMap.size && !this._search.length
  }

  get isNoSearchResults() {
    return !this.isLoading && !this._clickToTextMap.size && this._search.length
  }

  get clickToText() {
    return Array.from(this._clickToTextMap.values())
  }

  get search() {
    return this._search
  }

  setSearch = (key: string) => {
    this._search = key
  }

  reactionLoadClickToText = () => {
    this._disposeLoadClickToText?.()
    this._disposeRequestParams?.()

    this._disposeRequestParams = reaction(
      () => this.requestParams,
      () => {
        this._requestLoadClickToText.setLoading(true)
        this._page = 1
      }
    )

    this._disposeLoadClickToText = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadClickToText.debounceLoadData()
    )
  }

  private _requestLoadClickToText = new RequestLoadData<boolean>({
    requestCallback: async ({ cancelRequest }, noLoading) => {
      runInAction(() => {
        if (noLoading) {
          this._requestLoadClickToText.setLoading(false)
        }
      })

      try {
        const { data } = await ClickToTextApi.getClickToText(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })

        this.setGetClickToTextResponse(data)
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 408) {
          logger.warn(
            'TABLE: click-to-text-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const newLimit = tableLimitStore.reduceLimit('click-to-text-table', this._limit)
          this._limit = newLimit
          tableLimitStore.setLimit('click-to-text-table', newLimit)
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        this._isInitialLoading = false
      })
    },
  })

  loadClickToText = async (noLoading?: boolean) => {
    return this._requestLoadClickToText.loadData(noLoading)
  }

  setGetClickToTextResponse = ({ data, meta }: IResponseGetClickToText) => {
    this._clickToTextMap.clear()
    this._tableStore.setRows([])

    this.setClickToTextMap(data)
    this._tableStore.setRows(this.clickToText)
    this._page = meta.current_page
    this._limit = meta.per_page
    this._total = meta.total
  }

  setClickToTextMap = (clickToTextResponse: IResponseClickToText[]) => {
    clickToTextResponse.forEach((clickToTextResponse) => {
      try {
        this._clickToTextMap.set(
          clickToTextResponse.id,
          new ClickToText(clickToTextResponse, this._entities)
        )
      } catch (e) {
        logger.error(e)
      }
    })
  }

  reactionClickToTextActionShown = (setIsClickToTextActionShown: ISetIsClickToTextActionShown) => {
    this._disposeClickToTextActionShown?.()
    this._disposeClickToTextActionShown = reaction(
      () => [this.isLoading, this._clickToTextMap.size],
      () => setIsClickToTextActionShown(!this.isClickToTextEmpty)
    )
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
    tableLimitStore.setLimit('click-to-text-table', limit)
  }

  dispose = () => {
    this.clearReactions()
    this._requestLoadClickToText.dispose()
    this._requestGetColumns.dispose()
  }

  clearReactions = () => {
    this._disposeClickToTextActionShown?.()
    this._disposeLoadClickToText?.()
    this._disposeRequestParams?.()
  }

  onDelete = (id: number) => {
    let noLoading = true
    this._clickToTextMap.delete(id)

    if (this._clickToTextMap.size === 0 && this.page > 1) {
      this._page -= 1
      noLoading = false
    }
    void this.loadClickToText(noLoading)
  }

  private _saveColumns = async (ids: string[]) => {
    try {
      await UsersApi.updateUsersUiSettings({
        source_type: 'click-to-text-table-columns',
        items: ids,
      })
    } catch (e) {
      logger.error(e)
    }
  }

  private _requestGetColumns = new RequestLoadData<undefined, string[] | undefined>({
    requestCallback: async ({ cancelRequest }, arg) => {
      const { data } = await UsersApi.getUsersUiSettings('click-to-text-table-columns', {
        cancelToken: cancelRequest.token,
      })

      if (!data.data) return

      return data.data.items
    },
  })

  private _getColumns = async () => {
    return this._requestGetColumns.loadData()
  }
}
